import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { performLogout } from '../../utils/logoutHandler';
import '../sidebar/sidebar.css';

const API_URL = process.env.REACT_APP_API_URL;

const OffSecStaffSidebar = () => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const firstName = localStorage.getItem('first_name') || '';
  const lastName = localStorage.getItem('last_name') || '';

  // AdminLTE's own treeview plugin (adminlte.js) only auto-wires
  // [data-widget="treeview"] elements present in the DOM at its one-time
  // page-load scan - this sidebar mounts later as a React component, so
  // (same as carausersidbar.js/VrmSidbar.jsx) the submenu toggle needs its
  // own click handler rather than relying on that auto-init.
  useEffect(() => {
    const $ = window.$;
    if (!$) return undefined;

    $('.sidebar').off('click.treeview').on('click.treeview', '.has-treeview > a', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();

      const parent = $(this).parent();
      const submenu = parent.find('> .nav-treeview');
      const isOpen = parent.hasClass('menu-open');

      if (isOpen) {
        parent.removeClass('menu-open menu-is-opening');
        submenu.slideUp();
      } else {
        parent.siblings('.has-treeview.menu-open')
          .removeClass('menu-open menu-is-opening')
          .find('> .nav-treeview')
          .slideUp();
        parent.addClass('menu-open menu-is-opening');
        submenu.slideDown();
      }
    });

    return () => {
      $('.sidebar').off('click.treeview');
    };
  }, []);

  const handleLogout = async () => {
    try {
      const uname = localStorage.getItem('username');
      await axios.post(`${API_URL}api/logout/`, { username: uname }, { withCredentials: true });
      await performLogout('OFFSEC_STAFF', null, true);
      toast.success('Logged out successfully.');
    } catch (error) {
      await performLogout('OFFSEC_STAFF', null, true);
    }
  };

  const confirmLogout = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will be logged out of your session!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, logout!',
      background: '#fff',
      color: '#333',
    }).then((result) => {
      if (result.isConfirmed) {
        handleLogout();
      }
    });
  };

  return (
    <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <aside id="mainSidebar" className="main-sidebar sidebar-dark-primary elevation-4" data-bs-theme="dark">
        <Link to="/offsec_staff_overview" className="d-flex align-items-center m-3">
          <img
            src={isHovered ? '../img/cyberinsurify_logo.png' : '../img/cyberinsurify-logo-small.png'}
            alt="CyberInsurify Logo"
            className={`logo-small brand-image ${isHovered ? 'hovered' : ''}`}
          />
          <span className="brand-text font-weight-light">
            <img src="../img/cyberinsurify_logo.png" alt="CyberInsurify Logo" className="brand-logo ml-4" />
          </span>
        </Link>
        <h6 className="fw-300 ml-3 fs-5">OffSec Team</h6>

        <div className="sidebar sidebar-font d-flex flex-column" style={{ height: 'calc(100% - 90px)' }}>
          <nav className="mt-2">
            <ul className="nav nav-pills nav-sidebar flex-column text-left" data-widget="treeview" role="menu" data-accordion="false">
              <li className="nav-item">
                <Link
                  to="/offsec_staff_overview"
                  className={`nav-link ${location.pathname === '/offsec_staff_overview' ? 'active' : ''}`}
                >
                  <i className="nav-icon fas fa-home me-2" />
                  <p>Dashboard</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/offsec_staff_dashboard"
                  className={`nav-link ${location.pathname === '/offsec_staff_dashboard' ? 'active' : ''}`}
                >
                  <img src="../img/checklist-icon.svg" alt="Service Requests Icon" className="icon-style me-2" />
                  <p>Service Requests</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/offsec_staff_engagements"
                  className={`nav-link ${location.pathname === '/offsec_staff_engagements' ? 'active' : ''}`}
                >
                  <i className="nav-icon fas fa-briefcase me-2" />
                  <p>Engagements</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/offsec_staff_findings"
                  className={`nav-link ${location.pathname === '/offsec_staff_findings' ? 'active' : ''}`}
                >
                  <i className="nav-icon fas fa-shield-alt me-2" />
                  <p>Findings and Remediation</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/offsec_staff_revalidation"
                  className={`nav-link ${location.pathname === '/offsec_staff_revalidation' ? 'active' : ''}`}
                >
                  <i className="nav-icon fas fa-sync-alt me-2" />
                  <p>Revalidation</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/offsec_staff_reports"
                  className={`nav-link ${location.pathname === '/offsec_staff_reports' ? 'active' : ''}`}
                >
                  <i className="nav-icon fas fa-file-alt me-2" />
                  <p>Reports</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/offsec_staff_schedule"
                  className={`nav-link ${location.pathname === '/offsec_staff_schedule' ? 'active' : ''}`}
                >
                  <i className="nav-icon fas fa-calendar-alt me-2" />
                  <p>Schedule</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/offsec_staff_clients"
                  className={`nav-link ${location.pathname === '/offsec_staff_clients' ? 'active' : ''}`}
                >
                  <i className="nav-icon fas fa-users me-2" />
                  <p>Clients</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/offsec_staff_activity"
                  className={`nav-link ${location.pathname === '/offsec_staff_activity' ? 'active' : ''}`}
                >
                  <i className="nav-icon fas fa-heartbeat me-2" />
                  <p>Activity</p>
                </Link>
              </li>

              {/*  My Settings */}
              <li className="nav-item has-treeview">
                <Link to="#" className="nav-link">
                  <span className="sidebar-icons">
                    <img src="../img/settings-icon.svg" alt="My Settings Icon" className="icon-style me-2" />
                  </span>
                  <p>
                    My Settings
                    <i className="right fas fa-angle-left" />
                  </p>
                </Link>
                <ul className="nav nav-treeview">
                  <li className="nav-item">
                    <Link
                      to="/offsec_staff_settings"
                      className={`nav-link ${location.pathname === '/offsec_staff_settings' ? 'active' : ''}`}
                    >
                      <img src="../img/sub-icon.png" alt="Profile Icon" className="sub-icon me-2" />
                      <p>Profile</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      to="/privacy"
                      className={`nav-link ${location.pathname === '/privacy' ? 'active' : ''}`}
                    >
                      <img src="../img/sub-icon.png" alt="Privacy Icon" className="sub-icon me-2" />
                      <p>Privacy</p>
                    </Link>
                  </li>
                </ul>
              </li>
            </ul>
          </nav>

          <nav className="mt-auto mb-3">
            <ul className="nav nav-pills nav-sidebar flex-column text-left">
              <li className="nav-item">
                <button className="nav-link d-flex align-items-center logout-btn" onClick={confirmLogout}>
                  <span className="sidebar-icons">
                    <img src="../img/logout-icon.png" className="icon-style me-2" alt="logout-icon" />
                  </span>
                  <p className="mb-0">
                    Logout
                    {(firstName || lastName) && (
                      <span className="logout-username">{firstName} {lastName}</span>
                    )}
                  </p>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </aside>
    </div>
  );
};

export default OffSecStaffSidebar;
